import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Box,
  Typography,
  Autocomplete,
  Divider,
  IconButton,
  InputAdornment,
  Tabs,
  Tab,
} from '@mui/material';
import { ContentCopy, Visibility, VisibilityOff, PersonAdd, Link as LinkIcon } from '@mui/icons-material';
import { getUsers, linkUserToCompany, createAuthUserForCompany } from '../../lib/api';

interface AuthUser {
  id: string;
  email: string;
  user_metadata: {
    full_name?: string;
    company_id?: string | number;
  };
  app_metadata: {
    role?: string;
  };
}

interface LinkAuthUserDialogProps {
  open: boolean;
  onClose: () => void;
  companyId: string | number;
  companyEmail?: string;
  companyName?: string;
  onUserLinked: () => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`link-user-tabpanel-${index}`}
      aria-labelledby={`link-user-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const LinkAuthUserDialog: React.FC<LinkAuthUserDialogProps> = ({
  open,
  onClose,
  companyId,
  companyEmail,
  companyName,
  onUserLinked,
}) => {
  const { t } = useTranslation();
  
  // Tab state - default to Create New User tab (index 1)
  const [tabValue, setTabValue] = useState(1);
  
  // Link existing user state
  const [allUsers, setAllUsers] = useState<AuthUser[]>([]);
  const [selectedUser, setSelectedUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string>('');
  
  // Create new user state
  const [creatingUser, setCreatingUser] = useState(false);
  const [createdPassword, setCreatedPassword] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [passwordCopied, setPasswordCopied] = useState(false);

  useEffect(() => {
    if (open) {
      fetchAuthUsers();
      // Reset state when opening - default to Create New User tab
      setTabValue(1);
      setSelectedUser(null);
      setError(null);
      setSuccess(false);
      setSuccessMessage('');
      setCreatedPassword(null);
      setShowPassword(false);
      setPasswordCopied(false);
    }
  }, [open]);

  const fetchAuthUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const users = await getUsers();
      // Filter users that are not linked to any company
      const availableUsers = users.filter(
        (user) => !user.user_metadata?.company_id
      );
      setAllUsers(availableUsers as AuthUser[]);
    } catch (err: any) {
      console.error('Error fetching auth users:', err);
      setError(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleLinkUser = async () => {
    if (!selectedUser) return;

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      await linkUserToCompany(String(companyId), selectedUser.id);
      setSuccess(true);
      setSuccessMessage(t('companies.userLinkedSuccess', 'User linked successfully!'));
      setTimeout(() => {
        onUserLinked();
        handleClose();
      }, 1500);
    } catch (err: any) {
      console.error('Error linking user:', err);
      setError(err.message || 'Failed to link user');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    if (!companyEmail) {
      setError(t('companies.noEmailError', 'Company does not have an email address. Cannot create auth user.'));
      return;
    }

    setCreatingUser(true);
    setError(null);
    setSuccess(false);

    try {
      const result = await createAuthUserForCompany(String(companyId));
      setCreatedPassword(result.tempPassword);
      setSuccess(true);
      setSuccessMessage(t('companies.userCreatedSuccess', 'Auth user created successfully! Please save the temporary password.'));
      // Don't auto-close - let admin copy the password first
    } catch (err: any) {
      console.error('Error creating user:', err);
      setError(err.message || 'Failed to create auth user');
    } finally {
      setCreatingUser(false);
    }
  };

  const handleClose = () => {
    setSelectedUser(null);
    setError(null);
    setSuccess(false);
    setSuccessMessage('');
    setCreatedPassword(null);
    setShowPassword(false);
    setPasswordCopied(false);
    setTabValue(1); // Reset to Create New User tab
    onClose();
  };

  const handleDoneAfterCreate = () => {
    onUserLinked();
    handleClose();
  };

  const handleToggleShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const handleCopyPassword = async () => {
    if (createdPassword) {
      try {
        await navigator.clipboard.writeText(createdPassword);
        setPasswordCopied(true);
        setTimeout(() => setPasswordCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy password:', err);
      }
    }
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setError(null);
    setSuccess(false);
    setSuccessMessage('');
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('companies.linkAuthUser', 'Link User to Company')}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {successMessage}
          </Alert>
        )}

        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="link or create user tabs">
            <Tab 
              icon={<LinkIcon />} 
              iconPosition="start" 
              label={t('companies.linkExisting', 'Link Existing User')} 
            />
            <Tab 
              icon={<PersonAdd />} 
              iconPosition="start" 
              label={t('companies.createNew', 'Create New User')} 
            />
          </Tabs>
        </Box>

        {/* Tab 1: Link Existing User */}
        <TabPanel value={tabValue} index={0}>
          {loading && !allUsers.length ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress size={30} />
            </Box>
          ) : (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('companies.selectUserToLink', 'Select an existing auth user to link to this company')}
              </Typography>

              <Autocomplete
                options={allUsers}
                value={selectedUser}
                onChange={(_, newValue) => setSelectedUser(newValue)}
                getOptionLabel={(option) => 
                  `${option.email} ${option.user_metadata?.full_name ? `(${option.user_metadata.full_name})` : ''}`
                }
                renderOption={(props, option) => (
                  <Box component="li" {...props} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {option.email}
                      </Typography>
                      {option.app_metadata?.role === 'admin' && (
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            bgcolor: 'warning.main', 
                            color: 'warning.contrastText', 
                            px: 1, 
                            py: 0.25, 
                            borderRadius: 0.5,
                            fontSize: '0.7rem',
                            fontWeight: 600
                          }}
                        >
                          ADMIN
                        </Typography>
                      )}
                    </Box>
                    {option.user_metadata?.full_name && (
                      <Typography variant="caption" color="text.secondary">
                        {option.user_metadata.full_name}
                      </Typography>
                    )}
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('companies.searchUser', 'Search User')}
                    placeholder={t('companies.searchByEmail', 'Search by email')}
                  />
                )}
                disabled={loading || success}
                noOptionsText={t('companies.noUsersAvailable', 'No unlinked users available')}
              />

              {selectedUser && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    {t('companies.selectedUser', 'Selected User')}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {selectedUser.email}
                    </Typography>
                    {selectedUser.app_metadata?.role === 'admin' && (
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          bgcolor: 'warning.main', 
                          color: 'warning.contrastText', 
                          px: 1, 
                          py: 0.25, 
                          borderRadius: 0.5,
                          fontSize: '0.7rem',
                          fontWeight: 600
                        }}
                      >
                        ADMIN
                      </Typography>
                    )}
                  </Box>
                  {selectedUser.user_metadata?.full_name && (
                    <Typography variant="body2" color="text.secondary">
                      {selectedUser.user_metadata.full_name}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          )}
        </TabPanel>

        {/* Tab 2: Create New User */}
        <TabPanel value={tabValue} index={1}>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('companies.createAuthUserDescription', 'Create a new auth user for this company using the company\'s email address.')}
            </Typography>

            {companyName && (
              <TextField
                label={t('companies.companyName', 'Company Name')}
                value={companyName}
                fullWidth
                disabled
                sx={{ mb: 2 }}
              />
            )}

            <TextField
              label={t('companies.companyEmail', 'Company Email')}
              value={companyEmail || t('companies.noEmail', 'No email available')}
              fullWidth
              disabled
              sx={{ mb: 2 }}
              error={!companyEmail}
              helperText={!companyEmail ? t('companies.emailRequired', 'Company must have an email to create an auth user') : ''}
            />

            {!success && !createdPassword && (
              <Button
                onClick={handleCreateUser}
                variant="contained"
                fullWidth
                disabled={creatingUser || !companyEmail}
                sx={{ gap: 1 }}
              >
                {creatingUser ? <CircularProgress size={20} color="inherit" /> : <PersonAdd sx={{ fontSize: 18 }} />}
                {creatingUser ? t('common.creating', 'Creating...') : t('companies.createAuthUser', 'Create Auth User')}
              </Button>
            )}

            {createdPassword && (
              <Box sx={{ mt: 2 }}>
                <Divider sx={{ mb: 2 }} />
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {t('companies.savePasswordWarning', 'Please save this temporary password. It will not be shown again!')}
                </Alert>
                
                <TextField
                  label={t('companies.temporaryPassword', 'Temporary Password')}
                  type={showPassword ? 'text' : 'password'}
                  value={createdPassword}
                  fullWidth
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleToggleShowPassword}
                          edge="end"
                          size="small"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                        <IconButton
                          onClick={handleCopyPassword}
                          edge="end"
                          size="small"
                          color={passwordCopied ? 'success' : 'default'}
                        >
                          <ContentCopy />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 2 }}
                />

                {passwordCopied && (
                  <Typography variant="caption" color="success.main" sx={{ display: 'block', mb: 2 }}>
                    {t('common.copiedToClipboard', 'Copied to clipboard!')}
                  </Typography>
                )}

                <Button
                  onClick={handleDoneAfterCreate}
                  variant="contained"
                  color="success"
                  fullWidth
                >
                  {t('common.done', 'Done')}
                </Button>
              </Box>
            )}
          </Box>
        </TabPanel>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose} disabled={loading || creatingUser}>
          {t('common.cancel', 'Cancel')}
        </Button>
        {tabValue === 0 && (
          <Button
            onClick={handleLinkUser}
            variant="contained"
            disabled={!selectedUser || loading || success}
          >
            {loading ? t('common.saving', 'Saving...') : t('companies.linkUser', 'Link User')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default LinkAuthUserDialog;
